import moment from 'moment'
import * as yup from 'yup'
import {isInviteCoworkerEmailExists, isUserExists} from 'services/registration.service'
import {BANNED_PASSWORDS} from 'config/constant'

export const userInfoValidationSchema = {
    firstName: yup
        .string()
        .min(1, 'First Name is invalid.')
        .max(15, 'First Name is invalid.')
        .required('First Name is required.'),
    lastName: yup
        .string()
        .min(1, 'Last Name is invalid.')
        .max(15, 'Last Name is invalid.')
        .required('Last Name is required.'),
    jobTitle: yup
        .string()
        .min(1, 'Job Title is invalid.')
        .max(15, 'Job Title is invalid.')
        .required('Job Title is required.'),
    businessEmailId: yup
        .string()
        .matches(/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/, 'Business Email ID is invalid.')
        .email('Business Email ID is invalid.')
        .required('Business Email ID is required.'),
    password: yup
        .string()
        .min(8, 'Minimum 8 characters required')
        .max(32, 'Maximum 32 characters allowed')
        .matches(/^[0-9a-zA-Z\-+_,;~@#$%^&*/:!]+$/, 'Password is invalid.')
        .matches(/^(?=.*[0-9])[0-9a-zA-Z\-+_,;~@#$%^&*/:!]+$/, 'At least 1 numeral required.')
        .test('password', 'Password is invalid.', (value: any) => !BANNED_PASSWORDS.includes(value))
        .required('Password is required.'),
    confirmPassword: yup
        .string()
        .min(8, '')
        .max(32, '')
        .oneOf([yup.ref('password')], 'Passwords do not match')
        .required('Confirm Password is required.'),
    localAirport: yup
        .object()
        .test('localAirport', 'Local Airport is invalid.', (value: any) => value?.countryCode)
        .required('Local Airport is required.'),
    phone: yup
        .string()
        .test('phone', 'Phone is invalid.', (value: any) => {
            const numeralValue = value.replace(/\D/g, '')
            return numeralValue.length >= 5 && numeralValue.length <= 25
        })
        .matches(/^[0-9 ()-]+$/, 'Phone is invalid.')
        .required('Phone is required.'),
    aggrement: yup.boolean().isTrue('Please agree to the terms and conditions.')
}

export const brancNumberValidationSchema = {
    branchNumber: yup.string().when('isBranchExist', (isBranchExist: string | any[], schema) => {
        // If it's an array, access its value accordingly
        const value = Array.isArray(isBranchExist) ? isBranchExist[0] : isBranchExist

        if (value === 'yes') {
            return schema
                .min(5, 'Branch Number is invalid.')
                .matches(/^[0-9]*$/, 'Branch Number is invalid.')
                .required('Branch Number is required.')
        }
        return schema.notRequired()
    })
}

export const iacNumberValidationSchema = {
    iacNumber: yup
        .string()
        .min(3, 'IAC Number is invalid.')
        .max(14, 'IAC Number is invalid.')
        .matches(/^[a-zA-Z0-9]+$/, 'IAC Number is invalid.')
        .required('IAC Number is required.')
}

export const branchInfoValidationSchema = {
    branchData: yup.array().min(1, 'Branch Details are required.'),
    branchAirport: yup
        .object()
        .test('branchAirport', 'Branch Airport is invalid.', (value: any) => value?.countryCode)
        .required('Branch Airport is required.'),
    companyType: yup
        .string()
        .oneOf(['Agent', 'Cosignee', 'Forwarder', 'Shipper'], 'Company Type is required.')
        .required('Company Type is required.'),
    companyName: yup
        .string()
        .min(3, 'Company Name is invalid.')
        .max(40, 'Company Name is invalid.')
        .matches(/^[A-Za-z0-9.\-& ]+$/, 'Company Name is invalid.')
        .required('Company Name is required.'),
    streetAddress: yup
        .string()
        .min(3, 'Street Address is invalid.')
        .max(40, 'Street Address is invalid.')
        .matches(/^[A-Za-z0-9.\-& ]+$/, 'Street Address is invalid.')
        .required('Street Address is required.'),
    state: yup
        .string()
        .min(2, 'State/Province is invalid.')
        .max(40, 'State/Province is invalid.')
        .matches(/^[A-Za-z0-9.\- ]+$/, 'State/Province is invalid.'),
    zip: yup
        .string()
        .min(1, 'Zip/Postal is invalid.')
        .max(25, 'Zip/Postal is invalid.')
        .matches(/^[A-Za-z0-9\- ]+$/, 'Zip/Postal is invalid.'),
    isShipper: yup.string().required('Known shipper declaration is required.')
}

const formatPhoneNumber = (phoneCode: string): string => {
    const value = phoneCode.split(':', 1)[0]
    return value
}

export const mapToUserProfile = (payload: any) => ({
    password: payload.password,
    firstName: payload.firstName,
    lastName: payload.lastName,
    jobTitle: payload.jobTitle,
    emailAddress: payload.businessEmailId,
    phoneNumber: '+' + formatPhoneNumber(payload.phoneCode) + ' ' + String(payload.phone),
    station: payload.localAirport.code,
    branchId: payload.branchNumber
})

export const mapToGuestProfile = (payload: any) => ({
    password: payload.password,
    firstName: payload.firstName,
    lastName: payload.lastName,
    jobTitle: payload.jobTitle,
    emailAddress: payload.businessEmailId,
    phoneNumber: '+' + formatPhoneNumber(payload.phoneCode) + ' ' + String(payload.phone),
    station: payload.localAirport.code
})

export const mapToBranchProfile = (payload: any) => ({
    branchName: payload.companyName, // TODO: Need to verify
    address1: payload.streetAddress,
    address2: payload.iacNumber,
    state: payload.state,
    zip: payload.zip,
    countryCode: payload.branchAirport.countryCode,
    station: payload.branchAirport.code,
    eawbdomStatus: '',
    eawbintlStatus: ''
})

export const mapToBranchRelationship = (payload: any, branchId: number) => {
    const role = payload.companyType.charAt(0)
    const temp: any = payload.branchData.filter((item) => item.isALL === true)
    if (temp.length > 0) {
        const finalPayload: any[] = []
        temp[0].carrier.forEach((carrierCode) => {
            const obj: any = {
                branchId,
                carrierCode,
                role
            }
            finalPayload.push(obj)
        })
        return finalPayload
    }
    const carriers = Array.from(new Set(payload.branchData.map((obj) => obj.carrier)))
    const finalPayload: any[] = []
    carriers.forEach((carrierCode) => {
        const obj: any = {
            branchId,
            carrierCode,
            role
        }
        finalPayload.push(obj)
    })
    return finalPayload
}

export const mapToBranchAccount = (payload: any, branchId: number) => {
    const finalPayload: any[] = []
    payload.branchData.forEach((item) => {
        if (item.isALL) {
            item.carrier.forEach((obj) => {
                const temp = {
                    branchId,
                    accountId: item.number,
                    carrierCode: obj,
                    accountDescription: item.description
                }
                finalPayload.push(temp)
            })
        } else {
            const temp = {
                branchId,
                accountId: item.number,
                carrierCode: item.carrier,
                accountDescription: item.description
            }
            finalPayload.push(temp)
        }
    })
    return finalPayload
}

export const mapToNewBranchCreationAPI = (payload: any) => ({
    adminProfile: mapToUserProfile(payload),
    branchAccount: mapToBranchAccount(payload, 0),
    branchProfile: mapToBranchProfile(payload),
    branchRelationships: mapToBranchRelationship(payload, 0)
})

export const inviteCoworkersValidationSchema = {
    email: yup
        .string()
        .matches(/^(?:\s*[\w.-]+@[\w.-]+\s*)(?:,\s*[\w.-]+@[\w.-]+\s*)*$/, 'Business Email ID is invalid.')
        .email('Business Email ID is invalid.')
    // .required('Business Email ID is required.')
}

export const getMaxLength = (id: string) => {
    switch (id) {
        case 'branchNumber':
            return 5
        case 'iacNumber':
        case 'number':
            return 14
        case 'lastName':
        case 'firstName':
        case 'jobTitle':
            return 15
        case 'phone':
        case 'zip':
        case 'description':
            return 25
        case 'companyName':
        case 'state':
        case 'streetAddress':
            return 40
        case 'businessEmailId':
            return 49
        default:
            return 100
    }
}

export const checkExpiry = (d, expiryDays) => {
    const date = new Date(d)
    const today = new Date()
    return !(
        today.getTime() >= date.getTime() &&
        today.getTime() <= moment(d).add(parseInt(expiryDays), 'days').toDate().getTime()
    )
}

export const isMailExists = async (email: string) => {
    const regex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
    if (!regex.test(email)) {
        return false
    }
    try {
        const data = await isUserExists(email)
        return data.data.successFlag ? data.data.response : false
    } catch (err) {
        return false
    }
}

export const validateEmail = (email: string) => {
    const regex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/
    if (!regex.test(email) || email.length > 50) {
        return false
    } else {
        return true
    }
}

export const isInviteCoworkerExists = async (email: string) => {
    try {
        const data = await isInviteCoworkerEmailExists(email)
        return data.data.successFlag ? data.data.response : false
    } catch (err) {
        return false
    }
}
